import { apiClient } from '@/utils/api';

export interface Practice {
  id: string;
  practiceNumber?: string;
  title: string;
  typeId: string;
  type: {
    id: string;
    name: string;
    code: string;
    description?: string;
    icon?: string;
    color?: string;
  };
  status: string;
  priority: string;
  clientId: string;
  client: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
  };
  description?: string;
  startDate: string;
  dueDate?: string;
  completedAt?: string;
  amount?: number;
  paidAmount?: number;
  isPaid: boolean;
  invoiceNumber?: string;
  notes?: string;
  assignedToId?: string | null;
  assignedTo?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  isHistorical?: boolean;
  interventionLocation?: string | null;
  documentPath?: string | null;
  documentPathSetAt?: string | null;
  folderFileCount?: number | null;
  folderFileCountAt?: string | null;
  parentId?: string | null;
  parent?: { id: string; title: string; practiceNumber?: string | null } | null;
  children?: Practice[];
  createdAt: string;
  updatedAt: string;
  documents?: any[];
  activities?: any[];
  appointments?: any[];
  _count?: {
    documents: number;
    activities: number;
    appointments: number;
    activitiesCompleted?: number;
    children: number;
  };
  timeline?: Array<{
    id: string;
    type: 'created' | 'started' | 'activity_completed' | 'completed' | 'status_changed' | 'appointment_created' | 'appointment_updated' | 'updated';
    title: string;
    description: string;
    date: string | Date;
    userId?: string;
    userName?: string;
    activityId?: string;
    activityTitle?: string;
    appointmentId?: string;
    appointmentTitle?: string;
  }>;
}

export interface PracticeQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  clientId?: string;
  type?: string;
  status?: string;
  priority?: string;
  startDate?: string;
  endDate?: string;
  isPaid?: boolean;
  viewMode?: 'active' | 'historical' | 'archived' | 'completed' | 'all';
}

export interface PracticeStats {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
  totalAmount: number;
  paidAmount: number;
  unpaidAmount: number;
}

export const practiceService = {
  async getPractices(params: PracticeQueryParams = {}) {
    const response = await apiClient.get('/practices', { params });
    return response.data;
  },

  async getPractice(id: string) {
    const response = await apiClient.get(`/practices/${id}`);
    return response.data;
  },

  async createPractice(data: Partial<Practice>) {
    const response = await apiClient.post('/practices', data);
    return response.data;
  },

  async updatePractice(id: string, data: Partial<Practice>) {
    const response = await apiClient.put(`/practices/${id}`, data);
    return response.data;
  },

  async deletePractice(id: string, confirmPracticeNumber: string) {
    const response = await apiClient.delete(`/practices/${id}`, {
      data: { confirmPracticeNumber },
    });
    return response.data;
  },

  async archivePractice(id: string) {
    const response = await apiClient.patch(`/practices/${id}/archive`);
    return response.data;
  },

  async getPracticeStats() {
    const response = await apiClient.get('/practices/stats');
    return response.data;
  },

  async updateFolderFileCount(id: string, fileCount: number) {
    const response = await apiClient.patch(`/practices/${id}/folder-file-count`, { fileCount });
    return response.data;
  },
};

